import { defineStore } from "pinia";
import { useFetch } from '#app';
import type { IFaxState, IFax, IDomainSettings, ISetting, IFilterFax, ISendFax } from "@/types/fax/Types";
import { useDateTimeManager } from "@/composable/useDateTimeManager";
import { useCookie } from "@/composable/useCookie";
import { useToastStore } from "@/stores/toast";

const baseUrl = import.meta.env['VITE_IPBX_URL']
// @ts-ignore
export const useFaxStore = defineStore("FaxStore", {
  state: (): IFaxState => ({
    faxes: [],
    reports: [],
    retrySendFax: "",
    loading: false,
    error: null,
    setting: {}
  }),
  getters: {},
  actions: {
    async getFaxesList() {
      this.loading = true;
      this.error = null;
      try {
        const { data, error } = await useFetch<IFax[]>("/api/fax/list");
        this.faxes = data.value ?? [];
      } catch (err: any) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    async getReports(params: IFilterFax) {
      const { dateToPersian } = useDateTimeManager();
      try {
        const data = await $fetch<any>("/api/fax/reports", {
          method: "POST",
          body: {
            ...params
          },
        });
        this.reports = data?.map((item: any, index: number) => {
          const fax = this.faxes?.find((fax: any) => fax.FaxUUID == item.FaxUUID);
          const dateTime = item.FaxDate.split("T")
          const fileName = item.FaxFile.split("/");
          return {
            index: params.page * 20 + index + 1,
            id: item.FaxLogUUID,
            status: item.FaxStatus,
            date: dateToPersian(dateTime[0]),
            time: dateTime[1].replace("Z", ""),
            number: item.FaxPeerNumber,
            extension: fax?.FaxExtension,
            file: item.FaxFile,
            fileName: fileName.at(-1),
            direction: item.FaxDirection
          }
        });
      } catch (err: any) {
        console.log(err);
      }
    },
    async getSetting() {
      try {
        const { data, error } = await useFetch<IDomainSettings>('/api/fax/setting/list');
        let targetSetting: ISetting[] = [];
        if (data.value?.domain_settings) {
          targetSetting = data.value?.domain_settings;
        } else if (data.value?.default_settings) {
          targetSetting = data.value?.default_settings;
        }
        this.setting = (targetSetting.find(
          (item: ISetting) => item.subcategory == "send_retry_limit"
        ) || null);
        this.retrySendFax = this.setting?.value ?? ""
      } catch (err: any) {
        console.log(err);
      }
    },
    async updateSetting(count: string) {
      this.setting.value = count
      try {
        const data = await $fetch<any>('/api/fax/setting/update', {
          method: "POST",
          body: {
            fax_settings: [this.setting],
          }
        });
        return true
      } catch (err: any) {
        console.log(err);
        return false
      }
    },
    async sendFax(params: ISendFax) {
      try {
        if (!params.file || !params.fax_uuid) return
        const { getCookie } = useCookie();
        const toastStore = useToastStore();

        const formData = new FormData();
        formData.append("fax_file", params.file);
        formData.append("fax_uuid", params.fax_uuid);
        formData.append("destination_number", params.destination_number);
        formData.append("description", params.description);
        formData.append("dtmf", params.dtmf);
        const data = await $fetch<IDomainSettings>(`${baseUrl}/fax/send`, {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${getCookie("accessToken")}`,
          },
        });
        toastStore.showAlert({
          title: "Fax Send Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true
      } catch (err: any) {
        console.log(err);
        return false
      }
    },
    async insertFax(params: any) {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<any>('/api/fax/insert', {
          method: "POST",
          body: params
        });
        this.faxes?.push({
          FaxUUID: data.value.fax_uuid,
          FaxName: params.name,
          FaxExtension: data.value.fax_extension,
          FaxEmail: params.emails,
        })
        toastStore.showAlert({
          title: "Fax Insert Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        return false
      }
    },
    async updateFax(params: any) {
      const toastStore = useToastStore();
      try {
        const { data, error } = await useFetch<any>('/api/fax/update', {
          method: "PUT",
          body: params
        });
        const fax = this.faxes.find((item: IFax) => item.FaxUUID == params.fax_uuid);
        if (fax) {
          Object.assign(fax, {
            FaxExtension: params.extension,
            FaxEmail: params.emails,
            FaxName: params.name,
          });
        }
        toastStore.showAlert({
          title: "Fax Update Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        return false
      }
    },
    async removeFax(id: string) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>("/api/fax/remove", {
          method: "POST",
          body: { id }
        });
        const findIndex = this.faxes.findIndex((item: IFax) => item.FaxUUID == id);
        if (findIndex != -1) {
          this.faxes.splice(findIndex, 1);
        }
        toastStore.showAlert({
          title: "Fax Delete Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        return false
      }
    },
    async removeFaxFile(id: string) {
      const toastStore = useToastStore();
      try {
        const data = await $fetch<any>("/api/fax/removeFile", {
          method: "POST",
          body: { id }
        });
        toastStore.showAlert({
          title: "Fax File Delete Successfully",
          showCloseButton: true,
          timerProgressBar: true,
        });
        return true
      } catch (err: any) {
        toastStore.showAlert({
          title: "Can Not Connect To Server",
          showCloseButton: true,
          timerProgressBar: true,
          type: "error"
        });
        return false
      }
    },
  },

  // persist: {
  //   storage: persistedState.localStorage,
  // },
});
